import { useMessageSource } from "react-message-source";
import * as Yup from "yup";
import { useAddressValidator } from "../shared/components/address-select/validator";
import useBirthDateValidator from "../shared/components/birth-date/validator";
import { useTextInputValidator } from "../shared/components/formik/text-input/validator";
import useGenderValidator from "../shared/components/gender/validator";
import { useMandatoryStringTypeValidator } from "../shared/components/validator";
import {
  isCompanyClassificationBeneficialOwnerCase,
  isCompanyClassificationControllingOwnersCase,
} from "./helpers";
import { CompanyClassification } from "../../api/commercial/generated-types";

function useOwnerValidationSchema() {
  return Yup.object().shape({
    gender: useGenderValidator(),
    nationality: useMandatoryStringTypeValidator(),
    birthdate: useBirthDateValidator(),
    firstName: useTextInputValidator(),
    lastName: useTextInputValidator(),
    fullStreet: useTextInputValidator(),
    address: useAddressValidator(),
  });
}

export function useRegulatoryInformationSoleProprietorshipValidationSchema() {
  const ownerValidationSchema = useOwnerValidationSchema();

  return Yup.object().shape({
    beneficialOwner: Yup.object().when(
      "isOwnerSameAsAdmin",
      ([isOwnerSameAsAdmin]) => {
        return Yup.object().shape({
          admin: isOwnerSameAsAdmin === "yes" && ownerValidationSchema,
          other: isOwnerSameAsAdmin === "no" && ownerValidationSchema,
        });
      },
    ),
  });
}

export function useRegulatoryInformationValidationSchema() {
  const { getMessage } = useMessageSource();
  const ownerValidationSchema = useOwnerValidationSchema();
  return Yup.object().shape({
    hasOwnOffice: Yup.boolean().required(
      getMessage("Validation.message-required"),
    ),
    employsOwnStaff: Yup.boolean().required(
      getMessage("Validation.message-required"),
    ),
    companyClassification: Yup.string()
      .oneOf(
        Object.values(CompanyClassification),
        getMessage("Validation.message-required"),
      )
      .required(getMessage("Validation.message-required")),
    controllerIdentification: Yup.object()
      .nullable()
      .when("companyClassification", ([companyClassification]) => {
        if (
          !isCompanyClassificationControllingOwnersCase(companyClassification)
        ) {
          return undefined;
        }
        return Yup.string().required(getMessage("Validation.message-required"));
      }),
    beneficialOwner: Yup.object()
      .nullable()
      .when(
        ["companyClassification", "isOwnerSameAsAdmin"],
        ([companyClassification, isOwnerSameAsAdmin]) => {
          if (
            !isCompanyClassificationBeneficialOwnerCase(
              companyClassification,
              false, // dedicated validation for sole p. no need to check here.
            )
          ) {
            return undefined;
          }
          return Yup.object().shape({
            admin: isOwnerSameAsAdmin === "yes" && ownerValidationSchema,
            other: isOwnerSameAsAdmin === "no" && ownerValidationSchema,
          });
        },
      ),
    controllingOwners: Yup.object()
      .nullable()
      .when(
        ["companyClassification", "isOwnerSameAsAdmin"],
        ([companyClassification, isOwnerSameAsAdmin]) => {
          if (
            !isCompanyClassificationControllingOwnersCase(companyClassification)
          ) {
            return undefined;
          }
          return Yup.object().shape({
            admin:
              isOwnerSameAsAdmin === "yes" &&
              Yup.array().of(ownerValidationSchema),
            other:
              isOwnerSameAsAdmin === "no" &&
              Yup.array().of(ownerValidationSchema),
          });
        },
      ),
  });
}
